import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useGetPostCategories } from "../../../hooks/blog/categories/useGetPostCategories";
import { useDeletePostCategory } from "../../../hooks/blog/categories/useDeletePostCategory";
import useTableActions from "../../../hooks/common/useTableActions";

import TableLayout from "../../global/Table/TableLayout";
import { PostCategoriesTableColumns } from "./Table/PostCategoriesTableColumns";
import PostCategoriesTableRow from "./Table/PostCategoriesTableRow";

const PostCategoriesTable = () => {
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");

    const { deleteCategoryMutate, isDeleting } = useDeletePostCategory();

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/blog/categories",
        deleteSingle: deleteCategoryMutate,
    });

    const params = useMemo(() => {
        return {
            page,
            per_page: perPage,
            search: search || undefined,
            sort_by: "created_at",
            sort_order: order,
        };
    }, [page, perPage, search, order]);

    const { categories, meta, isLoading, error } = useGetPostCategories(params);

    const renderRow = (rowProps) => (
        <PostCategoriesTableRow
            {...rowProps}
            item={rowProps.item}
            isSelected={selectedIds.includes(rowProps.item.id)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
        />
    );

    const title = isArabic ? "تصنيفات المدونة" : "Blog Categories";
    const subtitle = isArabic ? "إدارة تصنيفات مقالات المدونة" : "Manage blog post categories";

    return (
        <TableLayout
            title={title}
            subtitle={subtitle}
            search={search}
            onSearchChange={setSearch}
            addButtonLabel={isArabic ? "إضافة تصنيف" : "Add Category"}
            onAddClick={() => navigate("/blog/categories/create")}
            isLoading={isLoading}
            error={error}
            items={categories}
            meta={meta}
            page={page}
            perPage={perPage}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            order={order}
            onRequestSort={handleRequestSort}
            selectedIds={selectedIds}
            onSelectAllClick={(e) => handleSelectAllClick(e, categories)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onBulkDelete={handleBulkDelete}
            columns={PostCategoriesTableColumns}
            renderRow={renderRow}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuOpen={handleMenuOpen}
            onMenuClose={handleMenuClose}
            onMenuEdit={handleEdit}
            onMenuDelete={handleMenuDelete}
            isDeletingSingle={isDeleting}
            emptyLabelAr="لا توجد تصنيفات حالياً"
            emptyLabelEn="No categories found"
        />
    );
};

export default PostCategoriesTable;
